import { AudioPlayer } from '#app/components/audio-player.tsx'
import { ScrollArea } from '#app/components/ui/scroll-area.tsx'

// Helper function to format time in MM:SS format
const formatTime = (seconds: number) => {
	const mins = Math.floor(seconds / 60)
	const secs = Math.floor(seconds % 60)
	return `${mins}:${secs.toString().padStart(2, '0')}`
}

interface TranscriptWord {
	word: string
	startTime: number
	endTime: number
	confidence?: number
}

interface TranscriptSegment {
	id: string
	speaker: {
		id: string
		name: string
		role: 'caller' | 'agent' | 'patient' | 'healthcare_provider'
		avatar?: string
	}
	startTime: number
	endTime: number
	words: TranscriptWord[]
}

interface Props {
	segments: TranscriptSegment[]
	currentTime?: number
	isPlaying?: boolean
	onSegmentClick?: (startTime: number) => void
	activeSegmentId?: string | null
	activeWordIndex?: number | null
	className?: string
	showTimestamps?: boolean
}

export function Transcript({
	segments,
	currentTime = 0,
	isPlaying = false,
	onSegmentClick,
	activeSegmentId,
	activeWordIndex,
	className = '',
	showTimestamps = true,
}: Props) {
	const renderHighlightedText = (segment: TranscriptSegment) => {
		if (!isPlaying || !segment.words || segment.words.length === 0) {
			return <span>{segment.words?.map((w) => w.word).join(' ') || ''}</span>
		}

		const isActiveSegment = activeSegmentId === segment.id

		return (
			<span>
				{segment.words.map((word, wordIndex) => {
					const isActiveWord = isActiveSegment && activeWordIndex === wordIndex
					const hasBeenSpoken = currentTime >= word.endTime
					const isPending = currentTime < word.startTime

					return (
						<span
							key={wordIndex}
							className={`transition-all duration-150 ${
								isActiveWord
									? 'rounded bg-yellow-200 px-0.5 font-medium dark:bg-yellow-800/50'
									: hasBeenSpoken
										? 'text-foreground'
										: isPending
											? 'text-muted-foreground'
											: 'text-foreground'
							}`}
						>
							{word.word}
							{wordIndex < (segment.words?.length || 0) - 1 ? ' ' : ''}
						</span>
					)
				})}
			</span>
		)
	}

	return (
		<div className={`flex h-full flex-col overflow-hidden ${className}`}>
			{segments.length > 0 ? (
				<div className="flex-1 overflow-hidden">
					<ScrollArea className="h-full px-3 py-1.5">
						<div className="space-y-4">
							{segments.map((segment) => {
								const isAgent = segment.speaker.role === 'agent'
								const isActive = activeSegmentId === segment.id

								return (
									<div
										key={segment.id}
										className={`group cursor-pointer rounded-lg p-4 transition-all duration-200 ${
											isActive
												? 'bg-primary/5 ring-primary/5 ring-2'
												: 'hover:bg-muted/50'
										} ${onSegmentClick ? 'cursor-pointer' : 'cursor-default'}`}
										onClick={() => onSegmentClick?.(segment.startTime)}
									>
										{/* Speaker header with timestamp */}
										<div className="mb-2 flex items-center justify-between">
											<div className="flex items-center gap-2">
												<div
													className={`h-2 w-2 rounded-full ${
														isAgent ? 'bg-blue-500' : 'bg-green-500'
													}`}
												/>
												<span className="text-sm font-medium">
													{segment.speaker.name}
												</span>
											</div>
											{showTimestamps && (
												<span className="text-muted-foreground text-xs">
													{formatTime(segment.startTime)}
												</span>
											)}
										</div>

										{/* Transcript text with word highlighting */}
										<div className="text-foreground text-sm leading-relaxed">
											{renderHighlightedText(segment)}
										</div>
									</div>
								)
							})}
						</div>
					</ScrollArea>
				</div>
			) : (
				<div className="text-muted-foreground flex h-full items-center justify-center">
					<p>Transcript will appear here during conversation</p>
				</div>
			)}
		</div>
	)
}

// Simple transcript segment interface for basic use cases (like hotline demo)
export interface SimpleTranscriptSegment {
	speaker: string
	text: string
	isAgent: boolean
	timestamp?: string
	words?: {
		word: string
		startTime: number
		endTime: number
	}[]
	segmentStartTime?: number
}

// PhoneSession transcript segment interface (matches demo+/types.ts PhoneSession.transcript)
export interface PhoneTranscriptSegment {
	speaker:
		| string
		| {
				id: string
				name: string
				role:
					| 'caller'
					| 'agent'
					| 'patient'
					| 'healthcare_provider'
					| 'AGENT'
					| 'Agent'
				avatar?: string
		  }
	startTime: number
	endTime: number
	text: string
}

interface SimpleTranscriptProps {
	segments: SimpleTranscriptSegment[]
	currentTime?: number
	isPlaying?: boolean
	className?: string
	showTimestamps?: boolean
}

export function SimpleTranscript({
	segments,
	currentTime = 0,
	isPlaying = false,
	className = '',
	showTimestamps = true,
}: SimpleTranscriptProps) {
	const renderHighlightedText = (segment: SimpleTranscriptSegment) => {
		if (!isPlaying || !segment.words || segment.words.length === 0) {
			return <span>{segment.text}</span>
		}

		return (
			<span>
				{segment.words.map((word, wordIndex) => {
					const isActiveWord =
						currentTime >= word.startTime && currentTime < word.endTime
					const hasBeenSpoken = currentTime >= word.endTime
					const isPending = currentTime < word.startTime

					return (
						<span
							key={wordIndex}
							className={`transition-all duration-150 ${
								isActiveWord
									? 'animate-pulse rounded bg-blue-200 px-1 font-medium text-blue-900'
									: hasBeenSpoken
										? 'text-foreground'
										: isPending
											? 'text-muted-foreground'
											: 'text-foreground'
							}`}
						>
							{word.word}
							{wordIndex < (segment.words?.length || 0) - 1 ? ' ' : ''}
						</span>
					)
				})}
			</span>
		)
	}

	return (
		<div className={`flex h-full flex-col overflow-hidden ${className}`}>
			{segments.length > 0 ? (
				<div className="flex-1 overflow-hidden">
					<ScrollArea className="h-full px-3 py-1.5">
						<div className="space-y-4">
							{segments.map((segment, index) => (
								<div
									key={index}
									className="group hover:bg-muted/50 cursor-pointer rounded-lg p-4 transition-all duration-200"
								>
									{/* Speaker header with timestamp */}
									<div className="mb-2 flex items-center justify-between">
										<div className="flex items-center gap-2">
											<div
												className={`h-2 w-2 rounded-full ${
													segment.isAgent ? 'bg-blue-500' : 'bg-green-500'
												}`}
											/>
											<span className="text-sm font-medium">
												{segment.speaker}
											</span>
										</div>
										{showTimestamps && segment.timestamp && (
											<span className="text-muted-foreground text-xs">
												{segment.timestamp}
											</span>
										)}
									</div>

									{/* Transcript text with word highlighting */}
									<div className="text-foreground text-sm leading-relaxed">
										{renderHighlightedText(segment)}
									</div>
								</div>
							))}
						</div>
					</ScrollArea>
				</div>
			) : (
				<div className="text-muted-foreground flex h-full items-center justify-center">
					<p>Transcript will appear here during conversation</p>
				</div>
			)}
		</div>
	)
}

// Phone transcript component for PhoneSession data
interface PhoneTranscriptProps {
	segments: PhoneTranscriptSegment[]
	currentTime?: number
	isPlaying?: boolean
	className?: string
	showTimestamps?: boolean
	audioPlayer?: {
		audioUrl?: string
		duration: number
		onTimeUpdate?: (time: number) => void
		onPlayStateChange?: (isPlaying: boolean) => void
		showSkipButtons?: boolean
		showSpeedControl?: boolean
		showDownload?: boolean
	}
}

export function PhoneTranscript({
	segments,
	currentTime = 0,
	isPlaying = false,
	className = '',
	showTimestamps = true,
	audioPlayer,
}: PhoneTranscriptProps) {
	return (
		<div className={`flex h-full flex-col overflow-hidden ${className}`}>
			{segments.length > 0 ? (
				<>
					<div className="flex-1 overflow-hidden">
						<ScrollArea className="h-full px-3 py-1.5">
							<div className="space-y-4">
								{segments.map((segment, index) => {
									// Handle both old format (string) and new format (Speaker object)
									const speakerName =
										typeof segment.speaker === 'string'
											? segment.speaker
											: segment.speaker.name || 'Unknown'
									const isAgent =
										typeof segment.speaker === 'string'
											? segment.speaker.toLowerCase().includes('agent')
											: segment.speaker.role === 'AGENT' ||
												segment.speaker.role === 'Agent' ||
												segment.speaker.role === 'agent'
									const isActive =
										isPlaying &&
										currentTime >= segment.startTime &&
										currentTime <= segment.endTime

									return (
										<div
											key={index}
											className={`group cursor-default rounded-lg p-4 transition-all duration-200 ${
												isActive ? 'bg-primary/5 ring-primary/5 ring-2' : ''
											}`}
										>
											{/* Speaker header with timestamp */}
											<div className="mb-2 flex items-center justify-between">
												<div className="flex items-center gap-2">
													<div
														className={`h-2 w-2 rounded-full ${
															isAgent ? 'bg-blue-500' : 'bg-green-500'
														}`}
													/>
													<span className="text-sm font-medium">
														{speakerName}
													</span>
												</div>
												{showTimestamps && (
													<span className="text-muted-foreground text-xs">
														{formatTime(segment.startTime)}
													</span>
												)}
											</div>

											{/* Transcript text */}
											<div className="text-foreground text-sm leading-relaxed">
												{segment.text}
											</div>
										</div>
									)
								})}
							</div>
						</ScrollArea>
					</div>

					{/* Audio Player */}
					{audioPlayer && (
						<div className="bg-background flex-shrink-0 border-t p-4">
							<AudioPlayer
								audioUrl={audioPlayer.audioUrl}
								duration={audioPlayer.duration}
								onTimeUpdate={audioPlayer.onTimeUpdate}
								onPlayStateChange={audioPlayer.onPlayStateChange}
								showSkipButtons={audioPlayer.showSkipButtons ?? true}
								showSpeedControl={audioPlayer.showSpeedControl ?? true}
								showDownload={audioPlayer.showDownload ?? true}
							/>
						</div>
					)}
				</>
			) : (
				<div className="text-muted-foreground flex h-full items-center justify-center">
					<p>Transcript will appear here during conversation</p>
				</div>
			)}
		</div>
	)
}
